import { Component, inject, signal, computed } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { UiSectionHeaderComponent } from '../../shared/components/ui-section-header/ui-section-header.component';
import { UtilisateursStore } from './utilisateurs.store';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-utilisateurs',
  imports: [TranslateModule, UiSectionHeaderComponent, CommonModule, FormsModule],
  providers: [UtilisateursStore],
  templateUrl: './utilisateurs.component.html',
  styleUrl: './utilisateurs.component.scss',
})
export class UtilisateursComponent {
  private readonly store = inject(UtilisateursStore);

  // Search signal
  searchEmail = signal('');

  // Public computed for template
  readonly isLoading = this.store.isLoading;
  readonly error = this.store.error;
  readonly allUtilisateurs = this.store.utilisateurs;

  // Filtered users based on email search
  readonly utilisateurs = computed(() => {
    const search = this.searchEmail().toLowerCase().trim();
    const allUsers = this.store.utilisateurs();

    if (!search) {
      return allUsers;
    }

    return allUsers.filter((user) => user.email?.toLowerCase().includes(search));
  });

  onRefresh(): void {
    this.store.reloadUtilisateurs();
  }

  onSearchChange(value: string): void {
    this.searchEmail.set(value);
  }

  exportToCSV(): void {
    // Export ALL users, not just filtered ones
    const users = this.store.utilisateurs();

    if (users.length === 0) {
      return;
    }

    // Create CSV header
    const headers = ['Matricule', 'Email', 'Intitulé de fonction', 'Dernière connexion'];
    const csvContent = [
      headers.join(';'),
      ...users.map((user) =>
        [
          this.escapeCsvValue(user.matric || ''),
          this.escapeCsvValue(user.email || ''),
          this.escapeCsvValue(user.functionTitle || ''),
          this.formatDate(user.lastLogin),
        ].join(';'),
      ),
    ].join('\n');

    // Create and download file
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `utilisateurs_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  private formatDate(dateString?: string): string {
    if (!dateString) {
      return '';
    }
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  private escapeCsvValue(value: string): string {
    if (value.includes(';') || value.includes('"') || value.includes('\n')) {
      return `"${value.replaceAll('"', '""')}"`;
    }
    return value;
  }
}
